import React, { forwardRef, useImperativeHandle } from 'react';
import { 
  Box, 
  TextField, 
  InputAdornment,
  alpha,
  useTheme 
} from '@mui/material';
import DatePicker from "react-multi-date-picker";
import { format, differenceInDays, isBefore } from 'date-fns';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './DateRangePicker.css';

/**
 * A reusable date range picker component that uses react-multi-date-picker
 * with validation for date ranges that don't exceed a maximum duration.
 * 
 * @param {Object} props - Component props
 * @param {Date|null} props.startDate - The start date
 * @param {Date|null} props.endDate - The end date
 * @param {Function} props.onChange - Callback function called when dates change
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {Object} props.errors - Error messages for validation
 * @param {string} props.label - Label for the date picker
 * @param {number} props.maxDays - Maximum number of days allowed (defaults to 14)
 */
const DateRangePicker = forwardRef(({ 
  startDate, 
  endDate, 
  onChange, 
  touched = false, 
  errors = {},
  label = "Travel Dates",
  maxDays = 14
}, ref) => {
  const theme = useTheme();
  const pickerRef = React.useRef();
  
  useImperativeHandle(ref, () => ({
    closeCalendar: () => {
      if (pickerRef.current) {
        pickerRef.current.closeCalendar();
      }
    }
  }));
  
  const handleDateRangeChange = (dateValues) => {
    console.log("DateRangeChange raw value:", dateValues);
    
    if (!dateValues || (Array.isArray(dateValues) && dateValues.length === 0)) {
      onChange(null, null);
      return;
    }
    
    try {
      // Handle range selection (array with two dates)
      if (Array.isArray(dateValues) && dateValues.length === 2 && dateValues[0] && dateValues[1]) {
        console.log("Processing date range:", dateValues);
        
        // Convert to JavaScript Date objects
        let start, end;
        
        // Handle first date - convert from DateObject format
        if (dateValues[0]) {
          try {
            if (typeof dateValues[0] === 'string') {
              start = new Date(dateValues[0]);
            } else if (dateValues[0] instanceof Date) {
              start = dateValues[0];
            } else if (dateValues[0].unix) {
              // Convert unix timestamp (seconds) to milliseconds
              start = new Date(dateValues[0].unix * 1000);
            } else if (dateValues[0].toDate) {
              // If it has a toDate method, use it
              start = dateValues[0].toDate();
            } else {
              // For DateObject from react-multi-date-picker
              const dateObj = dateValues[0];
              start = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
            }
          } catch (e) {
            console.error("Error parsing start date:", e);
            start = new Date();
          }
        }
        
        // Handle second date - convert from DateObject format
        if (dateValues[1]) {
          try {
            if (typeof dateValues[1] === 'string') {
              end = new Date(dateValues[1]);
            } else if (dateValues[1] instanceof Date) {
              end = dateValues[1];
            } else if (dateValues[1].unix) {
              // Convert unix timestamp (seconds) to milliseconds
              end = new Date(dateValues[1].unix * 1000);
            } else if (dateValues[1].toDate) {
              // If it has a toDate method, use it
              end = dateValues[1].toDate();
            } else {
              // For DateObject from react-multi-date-picker
              const dateObj = dateValues[1];
              end = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
            }
          } catch (e) {
            console.error("Error parsing end date:", e);
            end = new Date();
          }
        }
        
        console.log("Converted range dates:", start, end);
        
        if (start && end) {
          // Check if the date range exceeds maxDays
          const daysDifference = differenceInDays(end, start);
          console.log("Days difference:", daysDifference);
          
          if (daysDifference > maxDays) {
            // If exceeds maxDays, adjust the end date to be maxDays days from start date
            const adjustedEnd = new Date(start);
            adjustedEnd.setDate(start.getDate() + maxDays);
            console.log(`Adjusted end date to ${maxDays}-day maximum:`, adjustedEnd);
            onChange(start, adjustedEnd);
          } else {
            onChange(start, end);
          }
          
          // Auto-close the calendar after selecting both dates
          if (pickerRef.current) {
            pickerRef.current.closeCalendar();
          }
        }
      } 
      // Handle single date selection
      else if (!Array.isArray(dateValues) || (Array.isArray(dateValues) && dateValues.length === 1)) {
        let selectedDate;
        const singleValue = Array.isArray(dateValues) ? dateValues[0] : dateValues;
        
        try {
          if (typeof singleValue === 'string') {
            selectedDate = new Date(singleValue);
          } else if (singleValue instanceof Date) {
            selectedDate = singleValue;
          } else if (singleValue && singleValue.unix) {
            selectedDate = new Date(singleValue.unix * 1000);
          } else if (singleValue && singleValue.toDate) {
            selectedDate = singleValue.toDate();
          } else if (singleValue && typeof singleValue === 'object') {
            // For DateObject from react-multi-date-picker
            const dateObj = singleValue;
            console.log("Raw DateObject:", dateObj);
            if (dateObj.year && typeof dateObj.month === 'number' && dateObj.day) {
              // Month in JS Date is 0-indexed, but in DateObject it's 1-indexed
              selectedDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
              console.log("Created date from object:", selectedDate);
            } else {
              selectedDate = new Date();
            }
          } else {
            selectedDate = new Date();
          }
        } catch (e) {
          console.error("Error parsing single date:", e);
          selectedDate = new Date();
        }
        
        console.log("Single date selected:", selectedDate);
        
        if (selectedDate) {
          // If we have a previous start date but no end date, this selection becomes the end date
          if (startDate instanceof Date && !endDate) {
            // Make sure the end date isn't before the start date
            if (isBefore(selectedDate, startDate)) {
              // If the selected date is before the existing start date, swap them
              onChange(selectedDate, startDate);
              // Auto-close the calendar after selecting both dates
              if (pickerRef.current) {
                pickerRef.current.closeCalendar();
              }
            } else {
              // Check if the range exceeds maxDays
              const daysDifference = differenceInDays(selectedDate, startDate);
              if (daysDifference > maxDays) {
                // Limit to maxDays
                const adjustedEnd = new Date(startDate);
                adjustedEnd.setDate(startDate.getDate() + maxDays);
                console.log(`Adjusted end date to ${maxDays}-day maximum:`, adjustedEnd);
                onChange(startDate, adjustedEnd);
              } else {
                // Normal case - set the end date
                onChange(startDate, selectedDate);
              }
              // Auto-close the calendar after selecting both dates
              if (pickerRef.current) {
                pickerRef.current.closeCalendar();
              }
            }
          } else {
            // Otherwise this selection becomes the start date and we clear the end date
            onChange(selectedDate, null);
            console.log("Updated start date to:", selectedDate);
            // Do not close the calendar - wait for end date selection
          }
        }
      }
    } catch (err) {
      console.error("Error parsing dates:", err);
    }
  };
  
  // Create custom input component for the date picker
  const DateRangePickerInput = ({ openCalendar, value }) => {
    // Format the display value with clear start and end dates
    let displayValue = "";
    
    // If we have both start and end dates, use those directly
    if (startDate instanceof Date && endDate instanceof Date) {
      try {
        const startDateStr = format(startDate, "MMM dd, yyyy");
        const endDateStr = format(endDate, "MMM dd, yyyy");
        displayValue = `${startDateStr} to ${endDateStr}`;
      } catch (err) {
        console.error("Error formatting dates:", err);
      }
    } 
    // If we only have start date
    else if (startDate instanceof Date) {
      try {
        displayValue = format(startDate, "MMM dd, yyyy");
      } catch (err) {
        console.error("Error formatting start date:", err);
      }
    }
    // Fallback to empty string
    else {
      displayValue = "";
    }
    
    // Determine error state
    const isMissingDatesError = touched && (!startDate || !endDate);
    const isRangeError = errors && errors.dateRange && startDate && endDate;
    const isError = isMissingDatesError || isRangeError;
    
    // Determine helper text
    let helperText = "";
    if (isMissingDatesError) {
      helperText = "Travel dates required";
    } else if (isRangeError) {
      helperText = errors.dateRange;
    } else if (startDate && !endDate) {
      helperText = `Select end date (max ${maxDays} days from start)`;
    } else if (startDate && endDate) {
      const days = differenceInDays(endDate, startDate);
      helperText = `${days} day${days !== 1 ? 's' : ''} trip (maximum ${maxDays} days)`;
    }
    
    return (
      <TextField
        fullWidth
        label={label}
        value={displayValue}
        onClick={openCalendar}
        required
        error={isError}
        helperText={helperText}
        placeholder="Select start and end dates"
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon color="primary" />
            </InputAdornment>
          )
        }}
        sx={{
          cursor: 'pointer',
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            width: '100%',
            '&.Mui-focused': {
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          },
        }}
      />
    );
  };
  
  return (
    <Box sx={{ position: 'relative', width: '100%', display: 'block' }}>
      <DatePicker
        ref={pickerRef}
        value={[
          startDate instanceof Date ? startDate : null,
          endDate instanceof Date ? endDate : null
        ].filter(Boolean)}
        onChange={(dateValues) => {
          console.log("DatePicker onChange triggered with:", dateValues);
          handleDateRangeChange(dateValues);
        }}
        onOpenPickNewDate={true}
        range
        rangeHover={true}
        dateSeparator=" to "
        minDate={new Date(new Date().setHours(0,0,0,0))}
        format="MMM DD, YYYY"
        render={<DateRangePickerInput />}
        highlightToday={true}
        weekDays={["S", "M", "T", "W", "T", "F", "S"]}
        calendarPosition="bottom-start"
        fixMainPosition={false}
        arrow={false}
        shadow={false}
        hideWeekDays={false}
        className="custom-calendar"
        animation
        zIndex={9999}
        containerClassName="date-range-picker-container"
        onOpen={() => {
          console.log("Calendar opened with current dates:", startDate, endDate);
        }}
        onClose={() => {
          console.log("Calendar closed with dates:", startDate, endDate);
          // Remove the auto-setting of end date to match start date
          // Let the user explicitly select both dates
        }}
        style={{
          width: "100%",
          height: "100%"
        }}
      />
    </Box>
  );
});

export default DateRangePicker; 